<template>
	<div class="app-select" ref="wrapper">
		<label v-if="label" class="app-select-label">
			{{ label }}
		</label>

		<!-- Trigger -->
		<div :class="`app-select-trigger flex items-center gap-4 bg-grey-1 rounded ${triggerClass}`" @click="toggle">  
			<div class="app-select-trigger-text text-xs">{{ displayLabel }}</div>
			<div class="app-select-arrow" :class="{ open: isOpen }">
				<IconArrow :color_prop="colorTheme" />
			</div>
		</div>

		<!-- Dropdown -->
		<div v-if="isOpen" :class="`app-select-dropdown bg-grey-1 rounded-lg ${dropdownClass}`" :style="dropdownStyle">
			<!-- Search -->
			<div v-if="searchable" class="app-select-search-wrapper">
				<input v-model="search" class="app-select-search" placeholder="Search..." />
			</div>

			<!-- Options -->
			<ul class="app-select-list text-xs">
				<label v-if="insideLabel" class="text-xs text-grey font-medium py-2 px-3">
					{{ insideLabel }}
				</label>

				<li v-for="opt in filteredOptions" :key="opt.value" class="app-select-item"
					:class="{ selected: isSelected(opt), 'text-grey': opt.disabled }" @click="!opt.disabled && selectOption(opt)">
					<span>{{ opt.label }}</span>
					<span v-if="multiple && isSelected(opt) && !opt.disabled" class="app-select-check">✔</span>
				</li>

				<li v-if="!filteredOptions.length" class="app-select-empty">
					No options found
				</li>
			</ul>
		</div>

		<!-- Measurer -->
		<div ref="measurer" class="app-select-measurer"></div>
	</div>
</template>

<script>
import IconArrow from './icons/IconArrow.vue'

export default {
	props: {
		label: { type: String, default: "Select an option" },
		options: { type: Array, required: true },
		value: { type: [String, Array], required: true },
		multiple: { type: Boolean, default: false },
		searchable: { type: Boolean, default: false },
		insideLabel: { type: String, default: '' },
		triggerClass: { type: String, default: '' },
		dropdownClass: { type: String, default: '' },
	},

	components: {
		IconArrow
	},

	data() {
		return {
			colorTheme: getComputedStyle(document.documentElement).getPropertyValue('--color-theme'),
			isOpen: false,
			search: "",
			dropdownStyle: {}
		}
	},

	computed: {
		filteredOptions() {
			const s = this.search.toLowerCase().trim()
			return !s
				? this.options
				: this.options.filter(o => o.label.toLowerCase().includes(s))
		},

		displayLabel() {
			if (this.multiple && Array.isArray(this.value)) {
				const labels = this.options
					.filter(o => this.value.includes(o.value))
					.map(o => o.label)
				return labels.length ? labels.join(", ") : "Select option(s)"
			}
			const selected = this.options.find(o => o.value === this.value)
			return selected ? selected.label : "Select an option"
		}
	},

	methods: {
		toggle() {
			this.isOpen = !this.isOpen
			if (this.isOpen) {
				this.search = ""
				this.$nextTick(this.adjustWidth)
			}
		},

		isSelected(option) {
			return this.multiple
				? Array.isArray(this.value) && this.value.includes(option.value)
				: this.value === option.value
		},

		selectOption(option) {
			if (!this.multiple) {
				this.$emit("input", option.value)
				this.isOpen = false
				return
			}

			let arr = Array.isArray(this.value) ? [...this.value] : []
			if (arr.includes(option.value)) {
				arr = arr.filter(v => v !== option.value)
			} else {
				arr.push(option.value)
			}
			this.$emit("input", arr)
		},

		adjustWidth() {
			const wrapper = this.$refs.wrapper
			const measurer = this.$refs.measurer

			let width = wrapper.offsetWidth

			this.options.forEach(o => {
				measurer.textContent = o.label
				width = Math.max(width, measurer.offsetWidth)
			})

			this.dropdownStyle = { width: width + "px" }
		},

		handleClickOutside(e) {
			if (!this.$refs.wrapper.contains(e.target)) {
				this.isOpen = false
			}
		}
	},

	mounted() {
		document.addEventListener("click", this.handleClickOutside)
	},

	beforeDestroy() {
		document.removeEventListener("click", this.handleClickOutside)
	}
}
</script>

<style scoped>
/* Wrapper */
.app-select {
	position: relative;
	font-weight: 500;
}

/* Label */
.app-select-label {
	display: block;
	font-size: 0.875rem;
	margin-bottom: 0.25rem;
	color: #ddd;
}

/* Trigger */
.app-select-trigger {
	background: #333;
	padding: 0.5rem 0.75rem;
	color: #fff;
	cursor: pointer;
	display: flex;
	justify-content: space-between;
}

.app-select-trigger-text {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

/* Arrow */
.app-select-arrow {
	transition: transform 0.25s ease;
}

.app-select-arrow.open {
	transform: rotate(180deg);
}

/* Dropdown */
.app-select-dropdown {
	position: absolute;
	top: 100%;
	left: 0;
	background: #333;
	margin-top: 0.25rem;
	z-index: 9999;
	max-height: 15rem;
	overflow-y: auto;
	box-shadow: -4px 4px 4px 0px #00000040;
}

/* Search */
.app-select-search-wrapper {
	padding: 0.5rem;
	border-bottom: 1px solid #444;
}

.app-select-search {
	width: 100%;
	padding: 0.375rem;
	background: #222;
	color: #fff;
	border-radius: 0.25rem;
}

/* List */
.app-select-list {
	margin: 0;
	padding: 0;
	list-style: none;
}

.app-select-item {
	padding: 0.5rem 0.75rem;
	cursor: pointer;
	display: flex;
	justify-content: space-between;
}

.app-select-item:hover {
	background: #444;
}
.app-select-item.selected {
	background: var(--color-theme);
}
.app-select-item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}
.app-select-check {
	color: #4da3ff;
}

.app-select-empty {
	padding: 0.5rem 0.75rem;
	color: #aaa;
}

/* Hidden width measurer */
.app-select-measurer {
	position: fixed;
	visibility: hidden;
	white-space: nowrap;
	top: 0;
	left: 0;
	padding: 0.375rem 0.75rem;
}
</style>
